/**
 * TokenDetailPage — Main token detail page composing header, stats, creator info,
 * price chart, and trade panel.
 *
 * Route: /token/$tokenId
 * Data flow: useTokenDetail(id) → distribute props to child components.
 * PriceChart owns its own data fetching via usePriceHistory.
 *
 * Layout:
 *   Desktop (lg+): Two-column grid — left (content), right (sidebar: TradePanel + CreatorInfo)
 *   Mobile: Single column — TradePanel between PriceChart and TokenDataTabs
 *
 * Story 4.2 — Tasks 1, 7 | Story 4.3 — Task 6 | Story 4.4 — Task 5 | Story 5.4 — Task 4
 */

import { useParams } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useEffect, useRef } from "react";

import { AnalyticsSection } from "../components/token/AnalyticsSection";
import { CreatorInfo } from "../components/token/CreatorInfo";
import { StatsGrid } from "../components/token/StatsGrid";
import { TokenDataTabs } from "../components/token/TokenDataTabs";
import { TokenDetailSkeleton } from "../components/token/TokenDetailSkeleton";
import { TokenHeader } from "../components/token/TokenHeader";
import { TokenNotFound } from "../components/token/TokenNotFound";
import { TradePanel } from "../components/trading/TradePanel";
import { useTokenDetail } from "../hooks/api/use-token-detail";
import { trackClientEvent } from "../lib/analytics";
import { fadeInUp } from "../lib/animations";

export function TokenDetailPage() {
	const { tokenId } = useParams({ strict: false });
	const { data, isLoading, error } = useTokenDetail(tokenId ?? "");
	const trackedRef = useRef(false);

	useEffect(() => {
		if (data && !trackedRef.current) {
			trackedRef.current = true;
			trackClientEvent("token_detail_viewed", {
				tokenId: data.metadata.id,
				contractAddress: data.metadata.contractAddress,
				dataSource: data.stats?.dataSource ?? "none",
			});
		}
	}, [data]);

	if (isLoading) {
		return <TokenDetailSkeleton />;
	}

	if (error || !data) {
		return <TokenNotFound />;
	}

	const { metadata, stats } = data;

	return (
		<motion.div
			variants={fadeInUp}
			initial="hidden"
			animate="visible"
			className="mx-auto max-w-7xl px-4 py-8"
		>
			<TokenHeader metadata={metadata} stats={stats} />

			<div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 lg:gap-8">
				{/* Main column: stats + chart */}
				<div className="space-y-6">
					<StatsGrid stats={stats} />
					<AnalyticsSection tokenId={metadata.id} />
				</div>

				{/* Sidebar: TradePanel + CreatorInfo together, sticky on desktop */}
				<div className="lg:row-span-2">
					<div className="lg:sticky lg:top-8 space-y-6">
						<TradePanel
							tokenId={metadata.id}
							tokenAddress={metadata.contractAddress}
							poolAddress={metadata.poolAddress}
							tokenSymbol={metadata.symbol}
						/>
						<CreatorInfo
							creatorWallet={metadata.creatorWallet}
							feeSplit={{
								creator: metadata.feeBpsCreator,
								treasury: metadata.feeBpsTreasury,
								protocol: metadata.feeBpsProtocol,
							}}
							contractAddress={metadata.contractAddress}
							createdAt={metadata.createdAt}
						/>
					</div>
				</div>

				{/* Main column: data tabs */}
				<div>
					<TokenDataTabs tokenId={metadata.id} />
				</div>
			</div>
		</motion.div>
	);
}
